//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  forwardRef,
  Ref,
  useImperativeHandle,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  PaginationState,
  getPaginationRowModel,
  VisibilityState,
  Row,
  FilterFnOption,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';

// Styled Components
import {
  ThStyled,
  TdStyled,
  ContainerStyled,
  FiltersContainerStyled,
  SubContainerFiltersStyled,
  TableContainerStyled,
  TrStyled,
  ContainerButtonsStyled,
  ShowFiltersButton,
  ArrowBottomStyled,
  SubContainerHead,
  DropDownInputCapSub,
  SearchableDropdownCapSub,
  ButtonIconBackgroundStyled,
  DropDownVisibilityColumnStyled,
  ContainerLoader,
} from './styles';

import {
  Table,
  Title,
  TextCell,
  InputRadioStyled,
  InputRadioContainer,
  TextHeader,
  Tbody,
} from '../components/MainStyledComponents';

import { DoubleTapRow } from '../components/DoubleTapRow';

// Components
import InputCalendar from '../../../inputs/Calendar';
import InputText from '../../../inputs/InputText';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputPK from '../../../inputs/InputPK';
import { SpecialTag } from '../../SpecialTag';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

// Types
import {
  tableProps,
  dataTable,
  imperativeRefPagination,
  option,
} from './types';
import { onChangeValue } from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import {
  columnID,
  columnNames,
  getColumnId,
  getColumnNames,
} from '../components/types';
import { variant } from '../../SpecialTag/types';
import { Order } from '../../../../types/orders';
import { Options } from '../../../inputs/DropDown/DropDownList/types';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Feature Flags
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
  useIsSmallDesktop,
} from '../../../../hooks/useMediaBreakPoints';

// Action
import { selectedRowModalTable } from '../../../../state/actions/ui/selectRowTableModal';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

// Data
import { getTrackOptions } from '../../../../utils/catalogLabels';
import { getStateOptionsByRoleAndEntityType } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, listingFiltersByRole } from '../types';
import { useMirroredCursor } from '../utils/useMirroredCursor';

// GQL
import { GET_SUB_CAPITOLS_BY_IDS } from '../../../../gql/queries/orders/predefinedData';
import { client } from '../../../../gql/client';
import { Loader } from '../../../inputs/Loader';

/**
 * Tarea #11 — Esta tabla NO usa `useServerPageSize`.
 *
 * Consumers (solo modales): `RelatesCommunicationModal`, `TransferSubOrderModal`,
 * y como tabla embebida en `RelatedCampaignTable`. No hace fetch con `take` —
 * el dispatch a `setCursor` solo persiste filtros + el `pageSize` interno de
 * TanStack para sync entre modales (no batch fetching). El selector 25/50/100
 * no aplica por la regla del proyecto (modales dimensionados fijos).
 *
 * Mismo razonamiento que RelatedCampaignTable: si un modal futuro necesita
 * selector, migra ese caso a una tabla server-side propia.
 */
const InputRadioDefaultTable = (
  {
    titleText,
    data,
    onReFetchData,
    namingPersistForm,
    onRefetchPagination,
    tableId,
    setIsSelectedRow,
    onDoubleTapAccept,
    disabledRowStates,
    ...props
  }: tableProps,
  innerRef: Ref<imperativeRefPagination>
): JSX.Element => {
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);
  const dataAll = data?.genericOrdersData ?? mocksTable;
  const roleActive = data?.roleActive;
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);

  console.log(
    '🎯 [InputRadioDefaultTable] Data count:',
    dataAll?.length,
    '| Loading:',
    isLoadingData
  );
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListClassificationRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListTrackRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefInput>(null);

  // Feature flags for filter/column visibility
  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );

  const isSmallDesktop = useIsSmallDesktop();
  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const dispatch = useDispatch();

  const [showFilters, setShowFilters] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    teamIds: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    track: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    state: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
  });
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>(
    isPortraitTablet
      ? showFilters
        ? {
          pageIndex: 0,
          pageSize: 5,
        }
        : {
          pageIndex: 0,
          pageSize: 8,
        }
      : isLandscapeTablet
        ? showFilters
          ? {
            pageIndex: 0,
            pageSize: 5,
          }
          : {
            pageIndex: 0,
            pageSize: 5,
          }
        : {
          pageIndex: 0,
          pageSize: 8,
        }
  );

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLandscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLandscapeTablet]);

  useLayoutEffect(() => {
    if (isPortraitTablet) {
      setPagination(
        showFilters
          ? {
            pageIndex: 0,
            pageSize: 5,
          }
          : {
            pageIndex: 0,
            pageSize: 8,
          }
      );
    } else if (isLandscapeTablet) {
      setPagination(
        showFilters
          ? {
            pageIndex: 0,
            pageSize: 5,
          }
          : {
            pageIndex: 0,
            pageSize: 8,
          }
      );
    } else if (isSmallDesktop) {
      setPagination(
        showFilters
          ? {
            pageIndex: 0,
            pageSize: 8,
          }
          : {
            pageIndex: 0,
            pageSize: 10,
          }
      );
    } else {
      setPagination({
        pageIndex: 0,
        pageSize: 10,
      });
    }
  }, [isPortraitTablet, isLandscapeTablet, isSmallDesktop, showFilters]);

  // Sync responsive pageSize to Redux cursor on mount/resize so the modal's
  // useQuery uses the correct take from the first render (not hardcoded takeModal=8)
  useEffect(() => {
    if (tableId && pageSize && pageSize !== globalCursor?.take) {
      dispatch(setCursor(tableId as string, { ...globalCursor, take: pageSize }));
    }
  }, [pageSize]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Feature flag column visibility
  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
      capitol: showCapitolFilter,
      subCapitol: showSubCapitolFilter,
    }));
  }, [showPredefinedFilter, showCapitolFilter, showSubCapitolFilter]);

  const columnHelper = createColumnHelper<dataTable>();

  const [sorting] = useState<SortingState>([
    { id: 'registerInitDate', desc: true },
  ]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: undefined,
        cell: ({ row }) => (
          <InputRadioContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <InputRadioStyled
              name="Input"
              {...{
                checked: row.getIsSelected(), // Returns whether or not the row is selected.
                disabled: !row.getCanSelect(), // Returns whether or not the row can be selected.
                indeterminate: row.getIsSomeSelected(), //Returns whether or not some of the row's sub rows are selected.
                onChange: row.getToggleSelectedHandler(), //Returns a handler that can be used to toggle the row.
              }}
            />
          </InputRadioContainer>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.state.label}
          </TextHeader>
        ),
        cell: (info) => (
          <SpecialTag sizeTag="small" variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>,
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.registerInitDate.label}
            </TextHeader>
          ),
          cell: (info) =>
            info.getValue() && (
              <TextCell size={'S'}>
                {format(
                  new Date(info.getValue() as string),
                  'dd/MM/yyyy - HH:mm'
                )}
              </TextCell>
            ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),

      columnHelper.accessor(getColumnId(columnNames.capitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.capitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.subCapitol.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.subCapitol.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.team.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.team.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.track.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.track.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.direction.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor('pkInit', {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.pkInit.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.pkEnd.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.pkEnd.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.creationDate.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {columnNames.creationDate.label}
          </TextHeader>
        ),
        cell: (info) =>
          info.getValue() && (
            <TextCell size={'S'}>
              {format(
                new Date(info.getValue() as string),
                'dd/MM/yyyy - HH:mm'
              )}
            </TextCell>
          ),
      }),
    ],
    [isPortraitTablet, translatedColumnNames]
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
      sorting,
    },
    filterFns,
    enableRowSelection: disabledRowStates
      ? (row) => !disabledRowStates.includes(row.original.state ?? '')
      : true,
    enableMultiRowSelection: false, //disables multiple row selection for all rows in the table
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  console.log(
    '📋 [InputRadioDefaultTable] Rendering',
    table.getRowModel().rows.length,
    'rows'
  );

  // This methods are to pass currentPage and pageCount values to parent component by props

  // This consts are the functions to expose with useImperativeHandle

  const handlePageIndex = useCallback((i: number) => {
    table.setPageIndex(i);
  }, []);

  const handleNext = useCallback(() => {
    table.nextPage();
  }, []);

  const handlePrevious = useCallback(() => {
    table.previousPage();
  }, []);

  useImperativeHandle(innerRef, () => {
    return {
      pageIndex: handlePageIndex,
      nextPage: handleNext,
      prevPage: handlePrevious,
    };
  }, [handlePageIndex, handleNext, handlePrevious]);

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    async (value) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexExpedition = value;
        return newData;
      });
      setExpedientDefault(value);
    },
    [
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
    ]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    async (value) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexType = value;
        return newData;
      });
      setIndexDefault(value);
    },
    [
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
    ]
  );

  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.classification,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
    ]
  );

  const capitolOptions = useCallback(() => {
    return data?.filtersDataOptions?.capitols.map((capitol) => ({
      label: capitol.name,
      value: capitol.id,
      subCapitolsIDs: capitol.subCapitolsIDs,
    }));
  }, [data?.filtersDataOptions?.capitols]);

  const concessionOptions = useCallback(() => {
    return data?.filtersDataOptions?.concessions.map((concession) => ({
      label: concession.name,
      value: concession.id,
    }));
  }, [data?.filtersDataOptions?.concessions]);

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    const options = concessionOptions();
    if (
      options &&
      options.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = options[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const initialSubCapitolsOptions = useCallback(() => {
    return data?.filtersDataOptions?.subCapitols?.map(
      (subCapitol: { name: string; id: string }) => ({
        label: subCapitol.name,
        value: subCapitol.id,
      })
    );
  }, [data?.filtersDataOptions?.subCapitols]);

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const teamOptions = useCallback(() => {
    return data?.filtersDataOptions?.teams?.map((team: { name: string; id: string }) => ({
      label: team.name,
      value: team.id,
    }));
  }, [data?.filtersDataOptions?.teams]);

  const classificationOptions = useMemo(
    () =>
      data?.filtersDataOptions?.classifications
        ?.slice()
        ?.sort((a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0))
        ?.map((classification) => ({
          label: `${classification.classificationCode} - ${classification.name}`,
          value: classification.id,
        })),
    [data?.filtersDataOptions?.classifications]
  );

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  const predefinedOrderOptions = data?.filtersDataOptions?.predefinedOrders?.map(
    (predefined: { id: string; name: string }) => ({
      label: predefined.name,
      value: predefined.id,
    })
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.state,
    ]
  );

  const onChangeSelectedCapitol = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option.subCapitolsIDs,
        },
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.capitolId = option.value;
        newData.subCapitolId = undefined;
        return newData;
      });

      inputSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
    ]
  );

  const onChangeSelectedSubcapitol = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.subCapitolId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  const onChangeSelectedTeam = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    []
  );

  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.predefinedOrderId = option.value;
        return newData;
      });
    },
    []
  );

  const onChangeSelectedClassification = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.classification = option.value;
        return newData;
      });
    },
    []
  );

  const onChangeSelectedTrack = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.track = option.value;
        return newData;
      });
    },
    []
  );

  const onChangeSelectedState = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.state = option.value;
        return newData;
      });
    },
    []
  );

  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      dispatch(
        selectedRowModalTable(namingPersistForm as string, row.original)
      );

      if (row.getIsSelected()) {
        setIsSelectedRow(true);
      } else {
        setIsSelectedRow(false);
      }
    },
    [namingPersistForm]
  );

  // Sync rowSelection → Redux for direct radio button clicks (stopPropagation prevents onSingleTap)
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter((key) => (rowSelection as Record<string, boolean>)[key]);
    if (selectedIds.length > 0) {
      const selectedRow = table.getRow(selectedIds[0]);
      if (selectedRow) {
        dispatch(
          selectedRowModalTable(namingPersistForm as string, selectedRow.original)
        );
        setIsSelectedRow(true);
      }
    } else {
      setIsSelectedRow(false);
    }
  }, [rowSelection, table, namingPersistForm, dispatch]);

  const onClearValue = useCallback(() => {
    inputCapitolRef.current?.clearSelected();
    inputSubcapitolRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    inputListTeamRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListTrackRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const options = concessionOptions();
    const hasOnlyOneConcession = options && options.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? options[0].value : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }

    const dataCursor = {
      take: pageSize,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      orderBy: 'desc',
      state: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      state: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      state: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
    }));
    setSubCapitolOptions(initialSubCapitolsOptions);
    onRefetchPagination();
  }, [concessionOptions, onChangeInputExpedient, onChangeInputIndex]);

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  const commonColumns = Object.values(columnNames).filter((column) =>
    table
      .getAllColumns()
      .map((column) => column.id)
      .includes(column.id)
  );

  // commonLabels definition removed

  const handleSearch = async () => {
    const dataCursor = {
      take: pageSize,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      concessionId: selectorData?.concessionId ?? selectorCursor?.concessionId,
      capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
      teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      track: selectorData?.track ?? selectorCursor?.track,
      orderBy: 'desc',
      state: selectorData?.state ?? selectorCursor?.state,
      pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
      pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
      pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
      pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      description:
        (selectorData?.description ?? selectorCursor?.description) || undefined,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
    };
    dispatch(setCursor(tableId as string, dataCursor));

    onRefetchPagination();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoadingData as boolean);

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersContainerStyled isLoading={isLoadingData as boolean}>
        <SubContainerHead>
          <Title component={'h3'} variant={'semiBold'}>
            {titleText}
          </Title>
          {isLandscapeTablet ? (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters}></ArrowBottomStyled>
            </ShowFiltersButton>
          ) : null}
        </SubContainerHead>
        {showFilters ? (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            <InputExpedient
              label={t('headers.expedient')}
              disabled={isLoadingData}
              expedient={expedientDefault}
              onChangeInputExpedient={onChangeInputExpedient}
              ref={inputExpedientRef}
            />

            <InputIndex
              label={t('headers.index')}
              disabled={isLoadingData}
              index={indexDefault}
              onChangeInputIndex={onChangeInputIndex}
              ref={inputIndexRef}
            />
            <InputCalendar
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeDateInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              disabled={isLoadingData}
            />
            {concessionOptions() && (concessionOptions()?.length ?? 0) > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                options={concessionOptions() as option[]}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {showCapitolFilter && (
              <SearchableDropdownCapSub
                inputSize="S"
                ref={inputCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={globalCursor?.capitolId ?? ''}
                options={capitolOptions() as option[]}
                onChangeSelected={onChangeSelectedCapitol as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {showSubCapitolFilter && (
              <SearchableDropdownCapSub
                inputSize="S"
                ref={inputSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={globalCursor?.subCapitolId ?? ''}
                options={
                  (subCapitolOptions as option[]) ?? initialSubCapitolsOptions()
                }
                onChangeSelected={onChangeSelectedSubcapitol as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoadingData}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            {teamOptions() && (teamOptions()?.length ?? 0) > 0 && (
              <MultiSelectDropdown
                inputSize="M"
                ref={inputListTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions() as Options}
                onChangeSelected={onChangeSelectedTeam}
                isDisabled={isLoadingData}
              />
            )}
            {showPredefinedFilter && predefinedOrderOptions && (predefinedOrderOptions?.length ?? 0) > 0 && (
              <SearchableDropdownCapSub
                inputSize="S"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            <SearchableDropdownCapSub
              inputSize="S"
              ref={inputListClassificationRef}
              labelText={t('tables:headers.classification')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.classification ?? ''}
              options={classificationOptions as Options}
              onChangeSelected={onChangeSelectedClassification as onChangeValue}
              isDisabled={isLoadingData}
            />
            <SearchableDropdownCapSub
              inputSize="S"
              ref={inputListTrackRef}
              labelText={t('forms:workOrder.roadType')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.track ?? ''}
              options={trackOptions as Options}
              onChangeSelected={onChangeSelectedTrack as onChangeValue}
              isDisabled={isLoadingData}
            />
            <DropDownInput
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor?.state ?? ''}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedState as onChangeValue}
              isDisabled={isLoadingData}
            />

            <ContainerButtonsStyled>
              <ButtonIconBackgroundStyled
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumnStyled
                columns={table.getAllColumns().slice(1)}
                columnNames={Object.fromEntries(table.getAllColumns().slice(1).map(c => [c.id, typeof c.columnDef.header === 'string' ? c.columnDef.header : translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackgroundStyled
                variant={'restart'}
                onClick={onReFetchData}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonsStyled>
          </SubContainerFiltersStyled>
        ) : null}
      </FiltersContainerStyled>
      <TableContainerStyled isLoading={isLoadingData as boolean}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <ThStyled key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </ThStyled>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              const isRowDisabled = disabledRowStates?.includes(row.original.state ?? '') ?? false;
              return (
                <DoubleTapRow
                  key={row.id}
                  isDisabled={isRowDisabled}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                    if (onDoubleTapAccept) {
                      onDoubleTapAccept();
                    }
                  }}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
    </ContainerStyled>
  );
};

export default forwardRef(InputRadioDefaultTable);
